<template>
  <div>
    <TempPasswordDialog ref="tempPasswordDialog" />
    <div v-if="loading" class="loading-container pt-12">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </div>
    <v-form
      ref="form"
      @submit.prevent="save"
      v-show="!loading"
      :disabled="$attrs.disabled"
    >
      <ErrorMessages :messages="errors"></ErrorMessages>
      <v-text-field
        v-model="form.username"
        :rules="$rules.required"
        label="Login"
        required
        dense
        outlined
        ref="username"
        :disabled="!isOriginalOrganization"
      ></v-text-field>

      <v-text-field
        v-model="form.name"
        :rules="$rules.required"
        label="Nome"
        required
        dense
        outlined
        :disabled="!isOriginalOrganization"
      ></v-text-field>

      <v-switch
        v-model="form.active"
        label="Ativo"
        color="primary"
        class="mt-0 mb-2"
        :disabled="!isOriginalOrganization"
      ></v-switch>

      <v-switch
        v-model="form.integration"
        label="Usuário para autenticação"
        color="primary"
        class="mt-0 mb-2"
        :disabled="!isOriginalOrganization"
      ></v-switch>

      <v-select
        label="Tipo"
        :items="userRoles"
        v-model="form.role"
        :rules="$rules.required"
        dense
        outlined
      ></v-select>

      <v-text-field
        v-model="form.email"
        :rules="$rules.email()"
        label="E-mail"
        placeholder="Digite seu e-mail de usuário"
        required
        dense
        outlined
      ></v-text-field>

      <v-row>
        <v-col class="pb-0">
          <v-text-field
            v-model="form.user_naja_code"
            label="Código usuário"
            v-mask="'############'"
            required
            dense
            outlined
          ></v-text-field>
        </v-col>
        <v-col class="pb-0">
          <v-text-field
            v-model="form.naja_code"
            label="Código médico"
            v-mask="'############'"
            required
            dense
            outlined
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="3" class="pt-0">
          <v-select
            label="UF CRM"
            :items="ufs"
            v-model="form.crm_uf"
            :disabled="!isOriginalOrganization"
            required
            dense
            outlined
          ></v-select>
        </v-col>
        <v-col class="pt-0">
          <v-text-field
            v-model="form.crm_number"
            v-mask="'############'"
            label="Número CRM"
            :disabled="!isOriginalOrganization"
            required
            dense
            outlined
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-text-field
            v-model="form.password"
            label="Senha"
            required
            dense
            type="password"
            outlined
            :disabled="!isOriginalOrganization"
          ></v-text-field>
        </v-col>
        <v-col class="pb-0">
          <v-text-field
            v-model="form.password_confirmation"
            label="Confirmar Senha"
            required
            dense
            type="password"
            outlined
            :disabled="!isOriginalOrganization"
          ></v-text-field>
        </v-col>
      </v-row>

      <nj-multiple-select-input
        ref="multipleSelectInput"
        label="Perfis de acesso"
        max-width="100%"
        v-model="form.roles"
        :objects="roles"
        :hide-details="false"
      />

      <slot></slot>
    </v-form>
  </div>
</template>
<style scoped>
.v-form {
  max-width: 600px;
}
</style>
<script>
import Api from '@/resources/NajaSocialApi'
import USER_ORGANIZATION_ENUM from '@/enumerations/user_organization_enum'
import UFS_ENUM from '@/enumerations/ufs_enum'
import TempPasswordDialog from './temp_password_dialog'
import ErrorMessages from '@/components/ErrorMessages'

export default {
  name: 'UsersForm',
  components: { TempPasswordDialog, ErrorMessages },
  data: () => ({
    userRoles: USER_ORGANIZATION_ENUM.rules.filter(x =>
      ['USER_ROLE_COLLABORATOR', 'USER_ROLE_DOCTOR'].includes(x.key)
    ),
    ufs: UFS_ENUM.ufs,
    saveLoading: false,
    loading: false,
    roles: [],
    errors: [],
    user: null,
    form: {
      username: null,
      password: '',
      password_confirmation: '',
    },
  }),

  computed: {
    isOriginalOrganization() {
      if (this.user === null) {
        return true
      }

      return (
        this.user?.original_organization_id ===
        this.$store.state.session.organization?.id
      )
    },

    isNew() {
      return this.$route.params.id == null
    },
  },

  mounted() {
    this.init()

    // add username event
    this.$nextTick(() => {
      const input = this.$refs.username.$el.querySelector('input')
      if (input) input.addEventListener('blur', this.usernameOnBlur)
    })
  },

  beforeDestroy() {
    // remove username event
    const input = this.$refs.username.$el.querySelector('input')
    if (input) input.removeEventListener('blur', this.usernameOnBlur)
  },

  methods: {
    usernameOnBlur(e) {
      this.fetchByUsername(e.target.value)
    },
    async init() {
      try {
        this.loading = true
        const rolesResponse = await Api.roles.search()
        this.roles = rolesResponse.data.roles.map(role => {
          return { value: role.id, text: role.name }
        })

        if (this.$route.params.id) {
          await this.fetch(this.$route.params.id)
        }
      } catch (e) {
        this.$root.$children[0].toast('Erro ao buscar dados')
        throw e
      } finally {
        this.loading = false
      }
    },

    _parseForm(data) {
      this.user = data
      if (data.crm != null && data.crm != '') {
        data.crm_uf = data.crm.substring(0, 2)
        data.crm_number = data.crm.substring(2)
      }

      this.form = { ...this.form, ...data }
    },

    async fetchByUsername(username) {
      const query = {
        page: 1,
        q: {
          role_in: USER_ORGANIZATION_ENUM.rules
            .filter(x =>
              ['USER_ROLE_COLLABORATOR', 'USER_ROLE_DOCTOR'].includes(x.key)
            )
            .map(x => x.value),
          user_username_eq: username,
        },
      }

      const response = await Api.userOrgaizations.search(query)
      const id = response.data.user_organizations?.[0]?.id
      if (id) {
        this.fetch(id)
      }
    },

    fetch(id) {
      this.loading = true
      return Api.userOrgaizations
        .fetch(id)
        .then(async response => {
          this._parseForm(response.data.user_organization)
        })
        .catch(error => {
          if (error.response) {
            this.$root.$children[0].toast('Erro ao buscar usuário')
          } else {
            throw error
          }
        })
        .finally(() => {
          this.$scrollTop()
          this.loading = false
        })
    },

    save() {
      this.saveLoading = true
      const crm =
        this.form.crm_uf != null && this.form.crm_number != null
          ? `${this.form.crm_uf}${this.form.crm_number}`
          : null
      const data = {
        user_organization: {
          email: this.form.email,
          naja_code: this.form.naja_code,
          role: this.form.role,
          roles: this.form.roles,
          user_naja_code: this.form.user_naja_code,
          crm: crm,
          user_attributes: {
            id: this.form.user_id,
            name: this.form.name,
            username: this.form.username,
            active: this.form.active,
            integration: this.form.integration,
          },
        },
      }

      const isPasswordEmpty =
        !this.form.password && !this.form.password_confirmation

      if (!isPasswordEmpty) {
        data.user_organization.user_attributes.password = this.form.password
        data.user_organization.user_attributes.password_confirmation =
          this.form.password_confirmation
      }

      Api.userOrgaizations
        .save(this.form.id, data)
        .then(response => {
          this._parseForm(response.data.user_organization)
          this.$root.$children[0].toast('Usuário salvo com sucesso')

          this.$router.push({
            name: 'UserShow',
            params: { id: this.form.id },
          })

          // if (isNew) {
          //   this.$refs.tempPasswordDialog.open(tempPassword, this.form.id)
          // } else {
          //   this.$router.push({
          //     name: 'UserShow',
          //     params: { id: this.form.id },
          //   })
          // }
        })
        .catch(error => {
          if (error.response) {
            this.$root.$children[0].toast('Erro ao salvar usuário')
            if (error.response.status != 500) {
              this.errors = error.response.data.errors

              this.$refs.form.validate()
            }
          } else {
            throw error
          }

          // throw error;
        })
        .finally(() => {
          this.$scrollTop()
          this.saveLoading = false
        })
    },
  },
}
</script>
